import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import LockIcon from "@material-ui/icons/Lock";
import classnames from "classnames";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts";
import { UNDEFINED_LOCK_OPTION } from "entities/charts";
import { updateChartByPath } from "store/features";
import { LockState } from "types/charts/locks";

import { useChartEntities } from "hooks/charts";

import { DataEnum, Format } from "models";

import { AxisInput } from "./AxisInput";
import "./AxisLocker.scss";
import { LockIconWrapper } from "./AxisLocker.styles";

export function YMinLocker({ onInputStateChange, yAxisIndex = 0 }) {
  const { id } = useChartsContext();
  const dispatch = useDispatch();
  const {
    chartType,
    options: optionString,
    locks,
    mpcLocks,
    multiProduct,
    logAxis
  } = useChartEntities(id);

  const isMultiProductActive = multiProduct?.active;
  // Right (secondary) y-axis locks route to locks.y2Min. TotalRateDate uses this
  // for Well Count; Rig/Frac Activity Over Time reuses it for Frac Crew Count (#2b).
  const isTotalRateDateSecondary =
    (chartType === EvaChart.TotalRateDate ||
      chartType === EvaChart.TotalRateDateMidstream ||
      chartType === EvaChart.RigFracActivity) &&
    yAxisIndex === 1;

  // Keep a ref to mpcLocks so the options-sync effect always reads the latest value
  // without adding mpcLocks to its dep array (which would cause a dispatch loop when
  // override === true: dispatch → mpcLocks changes → effect re-runs → dispatch again).
  const mpcLocksRef = useRef(mpcLocks);
  mpcLocksRef.current = mpcLocks;

  // Parse optionString once per render — shared by all three effects and the JSX.
  const parsedAxis = useMemo(() => {
    const defaultReturn = {
      yAxisData: undefined,
      lockOptions: UNDEFINED_LOCK_OPTION,
      productKey: undefined
    };
    if (optionString.length === 0) return defaultReturn;
    try {
      const options = JSON.parse(optionString);
      const yAxisData = Array.isArray(options.yAxis)
        ? options.yAxis[yAxisIndex]
        : options.yAxis;
      const lockOptions = yAxisData?.lock ?? UNDEFINED_LOCK_OPTION;
      return {
        yAxisData,
        lockOptions,
        productKey: lockOptions?.productKey as string | undefined
      };
    } catch {
      return defaultReturn;
    }
  }, [optionString, yAxisIndex, chartType]);

  const [showLock, setShowLock] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [yMin, setYMin] = useState<LockState>(undefined);
  const [showInput, setShowInput] = useState(false);
  const isLogAxisOn = logAxis?.properties?.y?.active;
  useEffect(() => {
    onInputStateChange?.(showInput);
  }, [showInput, onInputStateChange]);
  useEffect(() => {
    if (optionString.length === 0) return;
    const { yAxisData, lockOptions, productKey } = parsedAxis;

    setShowLock(lockOptions.show);
    setYMin(lockOptions.min);
    const override = lockOptions?.min?.override ?? false;

    if (override && yAxisData?.min !== undefined && !isLogAxisOn) {
      if (isTotalRateDateSecondary) {
        if (locks?.y2Min?.type !== DataEnum.Reset) {
          dispatch(
            updateChartByPath({
              id,
              path: `locks.y2Min`,
              value: { value: yAxisData.min, type: lockOptions.min.type }
            })
          );
        }
      } else if (isMultiProductActive && productKey) {
        const existingMin = mpcLocksRef.current?.y?.[productKey]?.min;
        if (existingMin?.type !== DataEnum.Reset) {
          dispatch(
            updateChartByPath({
              id,
              path: `mpcLocks.y.${productKey}.min`,
              value: { value: yAxisData.min, type: lockOptions.min.type }
            })
          );
        }
      } else if (locks?.yMin?.type !== DataEnum.Reset) {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.yMin`,
            value: { value: yAxisData.min, type: lockOptions.min.type }
          })
        );
      }
    }
  }, [optionString, yAxisIndex, isMultiProductActive, isTotalRateDateSecondary]);

  useEffect(() => {
    if (isTotalRateDateSecondary) {
      setIsLocked(locks?.y2Min?.value !== undefined);
    } else if (isMultiProductActive) {
      const axisMinLock = parsedAxis.productKey
        ? mpcLocks?.y?.[parsedAxis.productKey]?.min
        : undefined;
      setIsLocked(axisMinLock?.value !== undefined);
    } else if (locks.yMin) {
      setIsLocked(locks.yMin.value !== undefined);
    }
  }, [locks, mpcLocks, isMultiProductActive, parsedAxis, isTotalRateDateSecondary]);

  const handleLockClick = () => {
    if (isLocked) {
      if (isTotalRateDateSecondary) {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.y2Min`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      } else if (isMultiProductActive && parsedAxis.productKey) {
        dispatch(
          updateChartByPath({
            id,
            path: `mpcLocks.y.${parsedAxis.productKey}.min`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      } else {
        dispatch(
          updateChartByPath({
            id,
            path: `locks.yMin`,
            value: { value: undefined, type: DataEnum.Reset }
          })
        );
      }
    } else {
      setShowInput(!showInput);
    }
  };

  const axisInputClassnames = classnames({ yAxis: true });
  const lockClassnames = classnames("axis-locker", "flex", {
    isLocked,
    yAxis: true,
    min: true
  });

  return (
    <>
      {showInput ? (
        <AxisInput
          axisInput={yMin}
          dataRange="yMin"
          lockPath={
            isTotalRateDateSecondary
              ? `locks.y2Min`
              : isMultiProductActive && parsedAxis.productKey
              ? `mpcLocks.y.${parsedAxis.productKey}.min`
              : undefined
          }
          setShowInput={setShowInput}
          className={classnames(axisInputClassnames, lockClassnames)}
          inputFormat={yMin?.format ?? Format.Number}
        />
      ) : (
        showLock && (
          <LockIconWrapper
            className={lockClassnames}
            onClick={handleLockClick}
            data-testid="y-min-locker">
            <LockIcon />
          </LockIconWrapper>
        )
      )}
    </>
  );
}
